/**
 * Service to interact with internal API proxies for Twilio Telephony and SMS.
 * This approach protects credentials and bypasses browser CORS restrictions.
 */
export const TwilioService = {
  /**
   * Initiates an outbound call to a client via internal serverless proxy.
   * Now passes full intake context for turn-based AI processing.
   */
  async initiateOutboundCall(phoneNumber: string, sessionId: string, intakeData?: any): Promise<any> {
    console.log(`[Twilio Service] Requesting outbound call to ${phoneNumber} (Session: ${sessionId})`);
    
    try {
      const response = await fetch('/api/twilio/call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          phoneNumber, 
          sessionId,
          clientName: intakeData?.client?.name,
          caseType: intakeData?.type,
          focusDetails: intakeData?.client?.focusDetails,
          language: intakeData?.client?.language || 'English'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Telephony bridge initiation failed via proxy');
      }

      return await response.json();
    } catch (error: any) {
      console.error('[Twilio Service] Proxy Error (Call):', error);
      throw error;
    }
  },

  /**
   * Sends an SMS message to a client via internal serverless proxy.
   */
  async sendSMS(phoneNumber: string, message: string): Promise<any> {
    console.log(`[Twilio Service] Requesting SMS to ${phoneNumber}`);
    
    try {
      const response = await fetch('/api/twilio/sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber, message })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'SMS delivery failed via proxy');
      }

      return await response.json();
    } catch (error: any) {
      console.error('[Twilio Service] Proxy Error (SMS):', error);
      throw error;
    }
  }
};